import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import notificationManager from '../utils/notificationManager';
import { playBeepSound, playOrderNotificationSound, playGeneralNotificationSound } from '../utils/notificationSound';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { vendor, isAuthenticated } = useAuth();
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const ringingOrdersRef = useRef({}); // { [orderId]: { intervalId, timeoutId, forceIntervalId } }

  const startRepeatRing = (orderId) => {
    stopRepeatRing(orderId);
    const intervalId = setInterval(() => {
      playOrderNotificationSound();
    }, 3 * 60 * 1000);
    const timeoutId = setTimeout(() => {
      stopRepeatRing(orderId);
      stopForceRing(orderId);
    }, 20 * 60 * 1000);
    ringingOrdersRef.current[orderId] = {
      ...(ringingOrdersRef.current[orderId] || {}),
      intervalId,
      timeoutId,
    };
  };

  const stopRepeatRing = (orderId) => {
    const entry = ringingOrdersRef.current[orderId];
    if (entry?.intervalId) clearInterval(entry.intervalId);
    if (entry?.timeoutId) clearTimeout(entry.timeoutId);
    if (entry) {
      delete entry.intervalId;
      delete entry.timeoutId;
    }
  };

  const forceOrderRing = (orderId) => {
    try {
      playOrderNotificationSound();
    } catch (e) {
      console.log('Force ring play failed:', e);
    }
    const forceIntervalId = setInterval(() => {
      try {
        playOrderNotificationSound();
      } catch (e) {
        console.log('Force ring play failed:', e);
      }
    }, 2500);
    ringingOrdersRef.current[orderId] = {
      ...(ringingOrdersRef.current[orderId] || {}),
      forceIntervalId,
    };
  };

  const stopForceRing = (orderId) => {
    const entry = ringingOrdersRef.current[orderId];
    if (entry?.forceIntervalId) clearInterval(entry.forceIntervalId);
    if (entry) delete entry.forceIntervalId;
  };
  const [notifications, setNotifications] = useState([]);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const audioRef = useRef(null);
  const recentEventRef = useRef(new Map());
  const getNotificationEventKey = (notification) => {
    if (!notification) return 'unknown';
    if (notification.type === 'new-order') {
      return `new-order-${notification.order?.id}`;
    }
    if (
      notification.type === 'order-status-update' ||
      notification.type === 'order-status-notification'
    ) {
      return `status-${notification.orderId}-${notification.status}`;
    }
    if (notification.type === 'order-reminder') {
      return `reminder-${notification.order?.id}`;
    }
    return `${notification.type}-${notification.orderId || notification.order?.id || 'generic'}`;
  };

  const isDuplicateNotificationEvent = (notification) => {
    const key = getNotificationEventKey(notification);
    const now = Date.now();
    const lastSeenAt = recentEventRef.current.get(key);

    // Suppress same event repeated within a short window.
    if (lastSeenAt && now - lastSeenAt < 5000) {
      return true;
    }

    recentEventRef.current.set(key, now);
    return false;
  };


  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
      
      if (Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
          setNotificationPermission(permission);
        });
      }
    }
  }, []);

  const playNotificationSound = (notificationType = 'general') => {
    // Check if sound notifications are enabled in settings
    const notificationSettings = JSON.parse(localStorage.getItem('notificationSettings') || '{"sound": true}');
    
    if (!notificationSettings.sound) {
      return;
    }
    
    try {
      // Use different sounds for different notification types
      if (
        notificationType === 'order' ||
        notificationType === 'new-order' ||
        notificationType === 'order-status-update' ||
        notificationType === 'order-status-notification' ||
        notificationType === 'order-reminder'
      ) {
        // Use MP3 file for order-related notifications
        playOrderNotificationSound();
      } else {
        // Use general notification sound for other notifications
        playGeneralNotificationSound();
      }
    } catch (error) {
      console.log('Error playing notification sound:', error);
    }
  };

  // Function to show browser notification
  const showBrowserNotification = (title, options = {}) => {
    // Check if browser notifications are enabled in settings
    const notificationSettings = JSON.parse(localStorage.getItem('notificationSettings') || '{"browser": true}');
    
    if ('Notification' in window && Notification.permission === 'granted' && notificationSettings.browser) {
      const notification = new Notification(title, {
        icon: '/favicon.ico', // Add your app icon
        badge: '/favicon.ico',
        tag: 'food-park-notification',
        requireInteraction: false,
        silent: false, // This will use system sound
        ...options
      });

      // Auto close after 5 seconds (only for non-interactive notifications)
      if (!options.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 5000);
      }

      // Handle click on notification
      notification.onclick = () => {
        window.focus();
        
        // Navigate based on notification type
        if (title.includes('New Order')) {
          // Extract order ID from title and navigate to specific order
          const orderIdMatch = title.match(/Order #(\d+)/);
          if (orderIdMatch) {
            const orderId = orderIdMatch[1];
            window.location.href = `/orders?order=${orderId}&action=view`;
          } else {
            window.location.href = '/orders';
          }
        } else if (title.includes('Status Updated')) {
          // Extract order ID from title and navigate to specific order
          const orderIdMatch = title.match(/Order #(\d+)/);
          if (orderIdMatch) {
            const orderId = orderIdMatch[1];
            window.location.href = `/orders?order=${orderId}&action=status`;
          } else {
            window.location.href = '/orders';
          }
        } else if (title.includes('Vendor Notification')) {
          // Navigate to dashboard for general vendor notifications
          window.location.href = '/dashboard';
        }
        
        notification.close();
      };

      return notification;
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !vendor) {
      return;
    }

    // Initialize socket connection
    const socketUrl = process.env.REACT_APP_SOCKET_URL || 
      (process.env.NODE_ENV === 'production' ? 'http://13.134.192.115' : 'http://localhost:3000');
    
    const newSocket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      maxReconnectionAttempts: 5,
    });

    // Connection events
    newSocket.on('connect', () => {
      console.log('🔌 Connected to WebSocket server');
      setIsConnected(true);
      
      // Authenticate vendor
      newSocket.emit('vendor-auth', vendor.id);
      
      // Join vendor room
      newSocket.emit('join-vendor-room', vendor.id);
      
      // Clear any existing reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Start ping interval
      pingIntervalRef.current = setInterval(() => {
        newSocket.emit('ping');
      }, 30000); // Ping every 30 seconds
    });

    newSocket.on('disconnect', () => {
      console.log('🔌 Disconnected from WebSocket server');
      setIsConnected(false);
      
      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('🔌 WebSocket connection error:', error);
      setIsConnected(false);
    });

    // Authentication events
    newSocket.on('auth-success', (data) => {
      console.log('🔐 Vendor authenticated successfully:', data);
    });

    newSocket.on('auth-error', (error) => {
      console.error('🔐 Vendor authentication error:', error);
    });

    // Room events
    newSocket.on('room-joined', (data) => {
      console.log('🏠 Joined vendor room:', data);
    });

    // Enhanced notification events
    newSocket.on('order-notification', (notification) => {
      console.log('📦 Received order notification:', notification);

      if (isDuplicateNotificationEvent(notification)) {
        return;
      }
      
      // Add notification to state
      setNotifications(prev => [notification, ...prev.slice(0, 9)]); // Keep last 10 notifications
      
      // Play notification sound with order type
      playNotificationSound(notification.type);
      
      // Any vendor-targeted event carrying a status that isn't a fresh
      // "new order" placement means the order has moved on - stop ringing
      // for it, regardless of the exact "type" field used.
      if (notification.status && notification.type !== 'new-order' && notification.orderId) {
        stopRepeatRing(notification.orderId);
        stopForceRing(notification.orderId);
      } else if (notification.order?.status && notification.order.status !== 'pending' && notification.order.status !== 'pending_approval' && notification.order?.id) {
        stopRepeatRing(notification.order.id);
        stopForceRing(notification.order.id);
      }

      // Show toast notification using notification manager
      if (notification.type === 'new-order') {
        notificationManager.showNewOrderNotification(notification.order);
        // Loud continuous ring immediately (every 2.5s, ignores mute) plus
        // a 3-min reminder ring as backup, both until confirmed (max 20 min).
        if (notification.order?.id) {
          forceOrderRing(notification.order.id);
          startRepeatRing(notification.order.id);
        }

        // Browser notification for new orders
        showBrowserNotification(
          `New Order #${notification.order.id}`,
          {
            body: `$${notification.order.total} • ${notification.order.orderItems?.length || 0} items • ${notification.order.order_type}`,
            icon: '/favicon.ico',
            badge: '/favicon.ico',
            tag: `new-order-${notification.order.id}`,
            requireInteraction: true // Require interaction for new orders
          }
        );

      } else if (notification.type === 'order-status-update' || notification.type === 'order-status-notification') {
        if (notification.orderId) {
          stopRepeatRing(notification.orderId);
          stopForceRing(notification.orderId);
        }
        notificationManager.showStatusUpdateNotification(notification.orderId, notification.status, notification.order);

        // Browser notification for status updates
        showBrowserNotification(
          `Order #${notification.orderId} Status Updated`,
          {
            body: `Status changed to: ${notification.status.replace('_', ' ')}`,
            icon: '/favicon.ico',
            tag: `status-update-${notification.orderId}`,
            requireInteraction: false
          }
        );
      } else if (notification.type === 'order-reminder') {
        notificationManager.showVendorNotification(
          `Order #${notification.order?.id} is still pending`,
          { id: `order-reminder-${notification.order?.id}` },
        );
      }
    });

    newSocket.on('vendor-notification', (notification) => {
      console.log('📢 Received vendor notification:', notification);
      
      // Add notification to state
      setNotifications(prev => [notification, ...prev.slice(0, 9)]);
      
      // Play notification sound with vendor notification type
      playNotificationSound('vendor-notification');
      
      // Show toast notification using notification manager
      notificationManager.showVendorNotification(notification.message, notification);

      // Browser notification
      showBrowserNotification(
        'Vendor Notification',
        {
          body: notification.message,
          icon: '/favicon.ico',
          tag: 'vendor-notification'
        }
      );
    });

    // Ping/pong for connection health
    newSocket.on('pong', () => {
      // Connection is healthy
    });

    setSocket(newSocket);

    // Cleanup function
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      newSocket.disconnect();
    };
  }, [isAuthenticated, vendor]);

  // Clear notifications
  const clearNotifications = () => {
    setNotifications([]);
  };

  // Remove specific notification
  const removeNotification = (index) => {
    setNotifications(prev => prev.filter((_, i) => i !== index));
  };

  // Request notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      return permission;
    }
    return 'denied';
  };

  const value = {
    socket,
    isConnected,
    notifications,
    notificationPermission,
    clearNotifications,
    removeNotification,
    requestNotificationPermission,
  };

  return (
    <SocketContext.Provider value={value}>
      {/* Hidden audio element for notification sounds */}
      <audio 
        ref={audioRef}
        preload="auto"
        style={{ display: 'none' }}
      >
        <source src="/notification-sound.mp3" type="audio/mpeg" />
        <source src="/notification-sound-1.mp3" type="audio/mpeg" />
      </audio>
      {children}
    </SocketContext.Provider>
  );
}; 